import { IconRefresh } from "@/assets/icons/geist/IconRefresh";
import { SadBox } from "@/assets/icons/SadBax";
import { Button } from "@/components/flexkit/Button";
import { Link } from "@/components/flexkit/Link";
import { Heading2 } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { formatError } from "@/utils/errors";
import { Code, ConnectError } from "@connectrpc/connect";
import { useCallback, type FC } from "react";

const formatErrorMessage = (
    error?: Error,
): { title?: string; message?: string; description?: string; retriable: boolean } => {
    let retriable = true;

    if (error instanceof ConnectError) {
        if (error.code === Code.NotFound) {
            return {
                title: "We could not find the project you're looking for.",
                description: "It may have been deleted, or you might be lacking permissions to access it.",
                retriable: false,
            };
        }
        if (error.code === Code.InvalidArgument) {
            retriable = false;
        }

        const message = formatError(error);
        return {
            message: message,
            retriable,
        };
    }

    return {
        retriable,
    };
};

type Props = {
    error?: Error | null;
};
export const ProjectFailedToLoad: FC<Props> = ({ error }) => {
    const onReload = useCallback(() => {
        window.location.reload();
    }, []);

    const { title, message, description, retriable } = formatErrorMessage(error ?? undefined);

    return (
        <div className="flex flex-col items-center gap-5 p-12" data-testid="error-failed-to-load-project-data">
            <SadBox />
            <div className="flex flex-col items-center gap-4">
                <Heading2 className="text-xl font-bold text-content-primary">
                    {title ?? "We're having trouble loading the project."}
                </Heading2>
                {message && (
                    <pre>
                        <code>{message}</code>
                    </pre>
                )}
                <Text className="text-lg text-content-secondary">
                    {description ?? "You can try refreshing the page. If the problem persists, please contact support."}
                </Text>
            </div>
            {retriable ? (
                <Button
                    variant="secondary"
                    LeadingIcon={IconRefresh}
                    onClick={onReload}
                    data-tracking-id="refresh-page-project-failed-to-load"
                >
                    Refresh Page
                </Button>
            ) : (
                <Button asChild>
                    <Link to="/projects">Back to Projects</Link>
                </Button>
            )}
        </div>
    );
};
